/**
 * Binding to `GET /api/organization/people`. The Rust API resolves the viewer, asks Fluxability
 * Accounts for the roster and returns it ready to render: labels, badge tones, filter groups and a
 * confirm link per allowed action. This module only moves that response into the page.
 *
 * @module apps/web/src/lib/organizationApi
 */

import { request } from '@/lib/api';

/** Visual weight chosen by the server for a badge or notice. */
export type OrganizationTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

/** One entry of a person's action menu. */
export interface OrganizationAction {
  /** Wire name of the action, such as `disable` or `reset_password`. */
  action: string;
  /** Menu label. */
  label: string;
  /** True for actions that take something away; shown in red below a separator. */
  destructive: boolean;
  /** Fluxability Accounts confirm page; the page navigates the whole tab here. */
  confirmUrl: string;
}

/** One row of the people table. */
export interface OrganizationPerson {
  /** FluxAccounts subject, used as the row key. */
  id: string;
  /** Display name, or the email address when no name is set. */
  name: string;
  /** Company email address. */
  email: string;
  /** Raw status, such as `active` or `invited`. */
  status: string;
  /** Status in plain words. */
  statusLabel: string;
  /** Status badge weight. */
  statusTone: OrganizationTone;
  /** Filter chip this row belongs to. */
  group: string;
  /** Raw role. */
  role: string;
  /** Role in plain words: Owner, Manager, Member or Blocked from inviting. */
  roleLabel: string;
  /** Last activity (RFC 3339), or null when unknown. */
  lastActiveAt: string | null;
  /** Invitation time (RFC 3339), or null. */
  invitedAt: string | null;
  /** Actions the signed-in manager may take now, in menu order. Empty means no menu. */
  actions: OrganizationAction[];
  /** Picture path under the API root when the person has a Fluxability picture, else null. */
  avatarUrl: string | null;
}

/** One roster page. */
export interface OrganizationRoster {
  /** Company shown. */
  company: {
    /** Company id, kept in the page URL. */
    id: string;
    /** Display name. */
    name: string;
    /** Email domain. */
    domain: string;
  };
  /** The signed-in manager. */
  viewer: {
    /** Subject. */
    id: string;
    /** `owner` or `manager`. */
    role: string;
    /** "Owner" or "Manager". */
    roleLabel: string;
  };
  /** People on this page. */
  people: OrganizationPerson[];
  /** Filter chips in display order; `all` first. */
  filters: {
    /** `all` or a group name matching `OrganizationPerson.group`. */
    id: string;
    /** Chip label. */
    label: string;
  }[];
  /** Token for the next page, or null on the last page. */
  nextCursor: string | null;
  /** Fluxability Accounts invite page. */
  inviteUrl: string;
  /** Why changes are confirmed on Fluxability Accounts. */
  confirmationNotice: string;
  /** Message about the confirmation the manager just returned from, or null. */
  notice: {
    /** Notice weight. */
    tone: OrganizationTone;
    /** Short title. */
    title: string;
    /** One sentence. */
    message: string;
  } | null;
}

/** Query sent to the roster route; every field is optional and passed through as-is. */
export interface OrganizationQuery {
  /** Company id from the page URL. */
  company?: string | null;
  /** Search text. */
  query?: string;
  /** Next-page token. */
  cursor?: string | null;
  /** `fluxaccounts_result` from a return URL. */
  fluxaccountsResult?: string | null;
  /** `action` from a return URL. */
  action?: string | null;
}

/** Roster route binding. */
export const organizationApi = {
  /** Fetch one roster page. Fails with 403 `ORGANIZATION_MANAGER_REQUIRED` for non-managers and 503 `ORGANIZATION_NOT_CONFIGURED` when the server is not set up. */
  people: (query: OrganizationQuery, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    if (query.company) params.set('company', query.company);
    if (query.query) params.set('query', query.query);
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.fluxaccountsResult) params.set('fluxaccounts_result', query.fluxaccountsResult);
    if (query.action) params.set('action', query.action);
    const suffix = params.toString();
    return request<OrganizationRoster>(`/organization/people${suffix ? `?${suffix}` : ''}`, { signal });
  },
};
